import React, { memo, useEffect, useMemo, useRef, useState } from 'react'

import { UserGroupIcon, UserIcon, UsersIcon } from '@heroicons/react/20/solid'
import { Field, Formik } from 'formik'
import { useRouter } from 'next/router'
import { toast } from 'react-toastify'
import * as yup from 'yup'

import useDocuments from '../../useDocuments'
import { setClosedLossDrawerOpen } from '@/client/generalSlice'
import { useAppDispatch, useAppSelector } from '@/client/hooks'
import { InputField, SelectField } from '@/components/FormikInputs'
import Stepper from '@/components/Stepper'
import { useCurrentApplication } from '@/services/application'
import { useCreateEnvelopeMutation } from '@/services/docusign'
import { useListApplicationProfilesQuery } from '@/services/v2-application/applicationProfile'
import { toastDefaultOptions } from '@/utils/toast'

const fileTypes = {
  initialApplicationforUnlockAgreement: 'Initial Application for Unlock Agreement',
  investmentClosingStatement: 'Investment Closing Statement',
  investmentEstimate: 'Investment Estimate',
  forwardSaleExchangeAgreement: 'Sample Forward Sale and Exchange Agreement',
  securityInstrument: 'Sample Performance Deed of Trust/Mortgage',
}

const compareDates = (documentA, documentB) => {
  const dateAObj = new Date(documentA.created_at)
  const dateBObj = new Date(documentB.created_at)
  return dateAObj.getTime() - dateBObj.getTime()
}

const getLatestDocument = (options) => {
  const latest = options.reduce(
    (documentA, documentB) => {
      return compareDates(documentA, documentB) > 0 ? documentA : documentB
    },
    { value: '', label: '', created_at: '' }
  )
  return latest.value
}

const SendToDocusign = ({ application, altFile, setIsOpen }) => {
  const dispatch = useAppDispatch()
  const formRef = useRef(null)
  const [message, setMessage] = useState('')
  const [isError, setIsError] = useState(false)
  const router = useRouter()

  const [createEnvelope] = useCreateEnvelopeMutation()
  const { data } = useCurrentApplication()
  const { data: applicationProfiles } = useListApplicationProfilesQuery(data?.uuid, {
    skip: !data?.uuid,
  })
  const fileModal = useAppSelector((state) => state?.fileManager?.activeModal?.file)
  const file = altFile ?? fileModal
  const selectedFiles = useAppSelector((state) => state.fileManager.selectedFiles)
  const selectedFile = file ? file : selectedFiles?.length ? selectedFiles[0] : undefined

  const { data: documents } = useDocuments()

  const documentOptions = useMemo(() => {
    const documentType = selectedFile?.fileName?.includes(
      fileTypes.initialApplicationforUnlockAgreement
    )
      ? fileTypes.initialApplicationforUnlockAgreement
      : selectedFile?.fileName?.includes(fileTypes.investmentEstimate)
        ? fileTypes.investmentEstimate
        : selectedFile?.fileName?.includes(fileTypes.investmentClosingStatement)
          ? fileTypes.investmentClosingStatement
          : null

    const options = documents
      ?.filter(
        (document) =>
          (documentType !== null
            ? document.fileName.includes(documentType)
            : document.fileName.includes(fileTypes.initialApplicationforUnlockAgreement) ||
              document.fileName.includes(fileTypes.investmentEstimate) ||
              document.fileName.includes(fileTypes.investmentClosingStatement)) &&
          document.tag !== 'Deleted'
      )
      ?.filter(
        (document) =>
          !(
            document.fileName.includes(fileTypes.investmentClosingStatement) &&
            (document.fileName.includes('Executed') || document.fileName.includes('Completed'))
          )
      )
      ?.map((document) => {
        return { label: document.fileName, value: document.uuid, created_at: document.created_at }
      })
    return options ? options : []
  }, [documents])

  const getInitialApplicants = () => {
    if (applicationProfiles?.count === 1) {
      return 'Single'
    }
    if (applicationProfiles?.count === 2) {
      return 'Joint'
    }
    if (applicationProfiles?.count === 3) {
      return selectedFile?.fileName?.includes(fileTypes.initialApplicationforUnlockAgreement)
        ? 'Multiple-3'
        : 'Multiple'
    }
    return 'Multiple'
  }

  const finalApplicationOptions = useMemo(() => {
    const options = documents
      ?.filter(
        (document) => document.fileName.includes('Final Application') && document.tag !== 'Deleted'
      )
      ?.map((document) => {
        return { label: document.fileName, value: document.uuid, created_at: document.created_at }
      })
    return options ? options : []
  }, [documents])

  const initialValues = {
    document: getLatestDocument(documentOptions),
    finalApplicationDocument: getLatestDocument(finalApplicationOptions),
    applicants: getInitialApplicants(),
    applicant: applicationProfiles?.results[0]?.profile?.email,
    coApplicant: applicationProfiles?.results[1]?.profile?.email,
    coApplicant2: applicationProfiles?.results[2]?.profile?.email,
    coApplicant3: applicationProfiles?.results[3]?.profile?.email,
  }

  const validationSchema = yup.object().shape({
    document: yup.string().required('Please select a document'),
    finalApplicationDocument: yup.string().when('document', {
      is: (document) =>
        documents
          .find((doc) => doc.uuid === document)
          ?.fileName.includes(fileTypes.investmentClosingStatement),
      then: yup
        .string()
        .required('Final Application must be generated in order to send an ICS package.'),
    }),
    applicant: yup.string().email().required(),
    coApplicant: yup
      .string()
      .email()
      .when('applicants', {
        is: (applicants) =>
          applicants === 'Joint' || applicants === 'Multiple-3' || applicants === 'Multiple',
        then: yup.string().required(),
      }),
    coApplicant2: yup
      .string()
      .email()
      .when('applicants', {
        is: (applicants) => applicants === 'Multiple-3' || applicants === 'Multiple',
        then: yup.string().required(),
      }),
    coApplicant3: yup
      .string()
      .email()
      .when('applicants', {
        is: (applicants) => applicationProfiles?.results?.length > 3 && applicants === 'Multiple',
        then: yup.string().required(),
      }),
  })

  const getTemplate = (document, applicants) => {
    const fileName = documents?.find((doc) => doc.uuid === document)?.fileName

    if (fileName?.includes(fileTypes.initialApplicationforUnlockAgreement)) {
      if (applicants === 'Single') {
        return 'Application - Single'
      }
      if (applicants === 'Joint') {
        return 'Application - Joint'
      }
      if (applicants === 'Multiple-3') {
        return 'Application - Multi-Applicants - 3'
      }

      if (applicants === 'Multiple') {
        return 'Application - Multi-Applicants'
      }
    }

    if (fileName?.includes(fileTypes.investmentEstimate)) {
      if (applicants === 'Single') {
        return 'Investment Estimate - Program Guide - Single'
      }
      if (applicants === 'Joint') {
        return 'Investment Estimate - Program Guide - Joint'
      }
      if (applicants === 'Multiple') {
        return 'Investment Estimate - Program Guide - Multi-Applicants'
      }
    }

    if (fileName?.includes(fileTypes.investmentClosingStatement)) {
      if (applicants === 'Single') {
        return 'Investment Closing Statement - Single'
      }
      if (applicants === 'Joint') {
        return 'Investment Closing Statement - Joint'
      }
      if (applicants === 'Multiple') {
        return 'Investment Closing Statement - Multi-Applicants'
      }
    }
  }

  useEffect(() => {
    setTimeout(() => {
      formRef?.current?.validateForm()
    }, 0)
  }, [formRef?.current])

  const resetDrawer = () => {
    setMessage('')
    setIsError(false)
    dispatch(setClosedLossDrawerOpen(false))
    toast.success('The file has been sent to DocuSign successfully.', { ...toastDefaultOptions })
  }

  const handleFinishAction = (handleSubmit) => (_, __) => {
    handleSubmit()
  }

  const handleClosedLoss = async (values, actions) => {
    try {
      actions.setSubmitting(true)
      setIsError(false)
      const templateName = getTemplate(values?.document, values?.applicants)

      const docs = [values?.document]

      if (
        documents
          ?.find((document) => document?.uuid === values?.document)
          ?.fileName?.includes(fileTypes.investmentClosingStatement)
      ) {
        docs.push(values?.finalApplicationDocument)
      }

      const response: any = await createEnvelope({
        documents: docs,
        templateName: templateName,
        signOnCX: false,
      })

      if (response.error) throw new Error(response.error)
      resetDrawer()
    } catch (error) {
      setIsError(true)
      setMessage('There was an error on the request, please try again')
      actions.setSubmitting(false)
    }
  }

  const getFirstStepValidation = (values, isClosingStatementDocument) => {
    if (isClosingStatementDocument) return !values.document || !values.finalApplicationDocument
    return !values.document
  }

  const getContent = (values, errors) => {
    const isApplicationDocument = documents
      ?.find((document) => document?.uuid === values?.document)
      ?.fileName?.includes(fileTypes.initialApplicationforUnlockAgreement)
    const isClosingStatementDocument = documents
      ?.find((document) => document?.uuid === values?.document)
      ?.fileName?.includes(fileTypes.investmentClosingStatement)

    const steps: any[] = [
      {
        title: 'Deal File Name',
        content: () => (
          <div className='flex flex-col gap-3'>
            <SelectField
              style='default'
              label='Select File'
              name='document'
              extraClasses='w-full'
              options={documentOptions}
            />
            {isClosingStatementDocument && (
              <>
                <SelectField
                  style='default'
                  label='Select Final Application'
                  name='finalApplicationDocument'
                  extraClasses='w-full'
                  options={finalApplicationOptions}
                />
              </>
            )}
          </div>
        ),
        buttonLabel: 'Next',
        disabledButton: getFirstStepValidation(values, isClosingStatementDocument),
      },
      {
        title: 'Number of Applicants',
        content: () => (
          <>
            <div
              role='group'
              aria-labelledby='number-of-applicants'
              className='grid grid-cols-3 gap-3'
            >
              <label className='w-max'>
                <Field
                  type='radio'
                  name='applicants'
                  value='Single'
                  className='hidden peer'
                />
                <div className='cursor-pointer px-2 flex justify-center items-center h-24 w-24 rounded-lg shadow-md text-center text-base text-gray-400 peer-checked:bg-sky-200 peer-checked:border peer-checked:border-[#3A7BBF] peer-disabled:bg-gray-400'>
                  <UserIcon
                    width={40}
                    fill='#4a5568'
                  />
                </div>
                <p className='text-base pt-2 text-gray-400 text-center'>Single</p>
              </label>
              <label className='w-max'>
                <Field
                  type='radio'
                  name='applicants'
                  value='Joint'
                  className='hidden peer'
                  disabled={applicationProfiles?.count < 2}
                />
                <div className='cursor-pointer px-2 flex justify-center items-center h-24 w-24 rounded-lg shadow-md text-center text-base text-gray-400 peer-checked:bg-sky-200 peer-checked:border peer-checked:border-[#3A7BBF] peer-disabled:bg-gray-400'>
                  <UsersIcon
                    width={40}
                    fill='#4a5568'
                  />
                </div>
                <p className='text-base pt-2 text-gray-400 text-center'>Joint</p>
              </label>
              <label className='w-max'>
                <Field
                  type='radio'
                  name='applicants'
                  value={isApplicationDocument ? 'Multiple-3' : 'Multiple'}
                  className='hidden peer'
                  disabled={applicationProfiles?.count < 3}
                />
                <div className='cursor-pointer px-2 flex justify-center items-center h-24 w-24 rounded-lg shadow-md text-center text-base text-gray-400 peer-checked:bg-sky-200 peer-checked:border peer-checked:border-[#3A7BBF] peer-disabled:bg-gray-400'>
                  <UserGroupIcon
                    width={40}
                    fill='#4a5568'
                  />
                </div>
                <p className='text-base pt-2 text-gray-400 text-center'>
                  Multiple {isApplicationDocument ? '(3)' : ''}
                </p>
              </label>
              {isApplicationDocument && (
                <label className='w-max'>
                  <Field
                    type='radio'
                    name='applicants'
                    value='Multiple'
                    className='hidden peer'
                    disabled={applicationProfiles?.count < 4}
                  />
                  <div className='cursor-pointer px-2 flex justify-center items-center h-24 w-24 rounded-lg shadow-md text-center text-base text-gray-400 peer-checked:bg-sky-200 peer-checked:border peer-checked:border-[#3A7BBF] peer-disabled:bg-gray-400'>
                    <UserGroupIcon
                      width={40}
                      fill='#4a5568'
                    />
                  </div>
                  <p className='text-base pt-2 text-gray-400 text-center'>Multiple (4)</p>
                </label>
              )}
            </div>
          </>
        ),
        buttonLabel: 'Next',
        disableButton: !values.applicants,
        disabledStep: getFirstStepValidation(values, isClosingStatementDocument),
      },
      {
        title: 'Recipients',
        content: () => (
          <div
            role='group'
            aria-labelledby='recipients'
            className='grid gap-3'
          >
            <div className='flex gap-3'>
              <div className='w-full'>
                <Field
                  label='Recipient 1'
                  component={InputField}
                  style='default'
                  parentClassName='w-full'
                  disabled
                  readonly
                  name='applicant'
                  value={applicationProfiles?.results[0]?.profile?.email}
                  showError={false}
                />
                {errors?.applicant && (
                  <p className='pb-2 text-base text-red-500'>
                    A valid email address is required{' '}
                    <button
                      type='button'
                      onClick={() => {
                        router.push(`/applications/${application?.externalId}#applicant-info-1`)
                        setIsOpen(false)
                      }}
                      className='text-blue-500 underline'
                    >
                      please update to continue
                    </button>
                  </p>
                )}
              </div>
            </div>

            {(values?.applicants === 'Joint' ||
              values?.applicants === 'Multiple-3' ||
              values?.applicants === 'Multiple') && (
              <>
                <div className='flex gap-3'>
                  <div className='w-full'>
                    <Field
                      label='Recipient 2'
                      component={InputField}
                      style='default'
                      parentClassName='w-full'
                      disabled
                      readonly
                      name='coApplicant'
                      value={applicationProfiles?.results[1]?.profile?.email}
                      showError={false}
                    />
                    {errors?.coApplicant && (
                      <p className='text-base text-red-500'>
                        A valid email address is required{' '}
                        <button
                          type='button'
                          onClick={() => {
                            router.push(`/applications/${application?.externalId}#applicant-info-2`)
                            setIsOpen(false)
                          }}
                          className='text-blue-500 underline'
                        >
                          please update to continue
                        </button>
                      </p>
                    )}
                  </div>
                </div>
              </>
            )}
            {(values?.applicants === 'Multiple-3' || values?.applicants === 'Multiple') && (
              <>
                <div className='flex gap-3'>
                  <div className='w-full'>
                    <Field
                      label='Recipient 3'
                      component={InputField}
                      style='default'
                      parentClassName='w-full'
                      disabled
                      readonly
                      name='coApplicant2'
                      value={applicationProfiles?.results[2]?.profile?.email}
                      showError={false}
                    />
                    {errors?.coApplicant2 && (
                      <p className='text-base text-red-500'>
                        A valid email address is required{' '}
                        <button
                          type='button'
                          onClick={() => {
                            router.push(`/applications/${application?.externalId}#applicant-info-3`)
                            setIsOpen(false)
                          }}
                          className='text-blue-500 underline'
                        >
                          please update to continue
                        </button>
                      </p>
                    )}
                  </div>
                </div>
              </>
            )}
            {values?.applicants === 'Multiple' && applicationProfiles?.results?.length === 4 && (
              <>
                <div className='flex gap-3'>
                  <div className='w-full'>
                    <Field
                      label='Recipient 4'
                      component={InputField}
                      style='default'
                      parentClassName='w-full'
                      disabled
                      readonly
                      name='coApplicant3'
                      value={applicationProfiles?.results[3]?.profile?.email}
                      showError={false}
                    />
                    {errors?.coApplicant3 && (
                      <p className='text-base text-red-500'>
                        A valid email address is required{' '}
                        <button
                          type='button'
                          onClick={() => {
                            router.push(`/applications/${application?.externalId}#applicant-info-4`)
                            setIsOpen(false)
                          }}
                          className='text-blue-500 underline'
                        >
                          please update to continue
                        </button>
                      </p>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        ),
        buttonLabel: 'Next',
        hideButton: true,
        disabledStep:
          getFirstStepValidation(values, isClosingStatementDocument) || !values.applicants,
      },
    ]
    return steps
  }

  return (
    <Formik
      enableReinitialize
      innerRef={formRef}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleClosedLoss}
    >
      {({ values, errors, isValid, isSubmitting, handleSubmit }) => (
        <Stepper
          steps={getContent(values, errors)}
          finishButton={{
            label: isError ? 'Retry - Send to Docusign' : 'Finish - Send to Docusign',
            onClick: handleFinishAction(handleSubmit),
            extraDisabled: isSubmitting || !isValid,
            footerMessage: message && (
              <div className={`${isError && 'text-red-500'}`}>{message}</div>
            ),
            loadingFooter: isSubmitting,
          }}
        />
      )}
    </Formik>
  )
}

export default memo(SendToDocusign)
